import { FC, RefObject } from "react";

import { Box, Stack, useTheme } from "@mui/material";
import { BannerSource, getYoutubeIdentifier, Ratio } from "@simplyk/common";
import { FieldError, UseFormReturn, useWatch } from "react-hook-form";

import { MAX_IMAGE_SIZES } from "../../../../../common/constants/image";
import { isYoutubeVideo } from "../../../../../common/helpers/validators";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { FormStepContainer } from "../FormStepContainer";

import { DisplayGrid } from "@/components/Display/DisplayGrid";
import { ImageUploader } from "@/components/ImageUploader/ImageUploader";
import { FundraiserContext, ImageUploadContext, ImageUploaderHandle } from "@/components/ImageUploader/types";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { BannerFormat, DashboardRadioGroup } from "@/dashboard-design-system/RadioGroup";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Delete as DeleteIcon } from "@/icons/outlined";

export type StepBannerFormData = {
  banner: string | null;
  bannerFormat: BannerFormat.Image | BannerFormat.Video;
  bannerVideoUrl?: string;
  isBannerUploading: boolean;
  /** Whether the form uses square banner format (1/1) - synced with DB */
  isSquareBanner?: boolean;
  /** AI-generated or library-provided alt text for the banner image — surfaced to donors for accessibility. */
  bannerAltText?: string | null;
};

export type LiveFormEditorV2StepBannerStatelessProps = {
  form: UseFormReturn<StepBannerFormData>;
  handleSaveAndNext: () => Promise<void>;
  handleSaveAndBack: () => Promise<void>;
  amplitudePayload: Record<string, unknown>;
  bannerRef: RefObject<ImageUploaderHandle | null>;
  isLegacyBanner?: boolean;
  onLegacyImageChange?: () => boolean | Promise<boolean>;
  /** Context for P2P/fundraiser campaign image uploads */
  fundraiserContext?: FundraiserContext;
  /** Callback when banner source changes (for tracking where the image came from) */
  onBannerSourceChange?: (source: BannerSource | null) => void;
  excludedZeffyImageTags?: string[];
};

export const LiveFormEditorV2StepBannerStateless: FC<LiveFormEditorV2StepBannerStatelessProps> = ({
  form,
  handleSaveAndNext,
  handleSaveAndBack,
  amplitudePayload,
  bannerRef,
  isLegacyBanner = false,
  onLegacyImageChange,
  fundraiserContext,
  onBannerSourceChange,
  excludedZeffyImageTags,
}) => {
  const { t } = useTranslate();
  const { logUserInputEvent } = useLiveFormAmplitudeEvents();
  const theme = useTheme();
  const { errors, isDirty } = form.formState;
  const isBannerUploading = useWatch({ control: form.control, name: "isBannerUploading" });

  const isBannerVideo = useWatch({ control: form.control, name: "bannerFormat" }) === BannerFormat.Video;
  const isSquareBanner = useWatch({ control: form.control, name: "isSquareBanner" });

  // Video banners always use 16/9 layout, otherwise use square if isSquareBanner is true
  const useSquareBanner = !isBannerVideo && isSquareBanner;
  const bannerVideoLink = useWatch({ control: form.control, name: "bannerVideoUrl" });
  const bannerValue = useWatch({ control: form.control, name: "banner" });

  // Show migration infobox for legacy banners (non-square, non-video)
  const showMigrationInfobox = isLegacyBanner && !isBannerVideo;

  const handleChangeBannerType = (value: string) => {
    if (value === BannerFormat.Video) {
      form.setValue("banner", null, { shouldDirty: true });
      form.setValue("bannerFormat", value, { shouldDirty: true });
    } else if (value === BannerFormat.Image) {
      form.setValue("bannerVideoUrl", "", { shouldDirty: true });
      form.setValue("bannerFormat", value, { shouldDirty: true });
    }
  };

  const deleteImage = async () => {
    if (isLegacyBanner && onLegacyImageChange) {
      await onLegacyImageChange();
    }
    form.setValue("banner", "", { shouldValidate: true, shouldDirty: true });
    form.setValue("bannerAltText", null, { shouldDirty: true });
    onBannerSourceChange?.(null);
    if (bannerRef.current) {
      bannerRef.current.setImage(null);
    }
  };

  const handleAltTextMetadataChange = (metadata: { altText: string | null; status?: "pending" | "ready" } | null) => {
    if (metadata?.status === "pending") {
      form.setValue("bannerAltText", null, { shouldDirty: true });
      return;
    }
    form.setValue("bannerAltText", metadata?.altText ?? null, { shouldDirty: true });
  };

  return (
    // Step container for the banner step
    <FormStepContainer
      title={t("dashboard", "liveFormEditor.stepBanner.title")}
      data-test="banner-step-live-form-editor"
      form={form}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
        disableNext: isBannerUploading,
        disableBack: isBannerUploading,
        nextButtonLabel: isDirty || bannerValue || isBannerVideo ? "next" : "skip",
      }}
    >
      {/* Migration infobox for legacy banners */}
      {showMigrationInfobox && (
        <DashboardInfobox vibe="brand" size="small" sx={{ marginBottom: 2 }}>
          {t("dashboard", "liveFormEditor.squareBannerMigration.infobox")}
        </DashboardInfobox>
      )}

      <LiveEditorBlock gap={1}>
        {/* Header: Banner label and delete button */}
        <Stack direction="row" justifyContent="space-between">
          <DashboardTypography variant="subtitle1">
            {t("dashboard", "liveFormEditor.stepBanner.banner")}
          </DashboardTypography>
          <DashboardIconButton size="small" onClick={deleteImage} vibe="neutral" data-test="delete-banner-button">
            <DeleteIcon />
          </DashboardIconButton>
        </Stack>
        {/* Banner type selection (Image/Video) */}
        <Stack
          sx={{
            width: "100%",
            ...(useSquareBanner && {
              maxWidth: 400,
              margin: "0 auto",
            }),
            gap: 2,
          }}
        >
          <Stack alignItems="center" justifyContent="center" width="100%">
            <Box>
              <DashboardRadioGroup
                row
                defaultValue={isBannerVideo ? BannerFormat.Video : BannerFormat.Image}
                values={{
                  [BannerFormat.Image]: t("dashboard", "bannerAndLogoPicker.image"),
                  [BannerFormat.Video]: t("dashboard", "bannerAndLogoPicker.video"),
                }}
                onChange={handleChangeBannerType}
                control={form.control}
                name="bannerFormat"
                separator={
                  <DashboardTypography variant="body2" sx={{ marginRight: 2, color: theme.palette.text.neutral.quiet }}>
                    {t("dashboard", "or")}
                  </DashboardTypography>
                }
              />
            </Box>
          </Stack>
          {/* Banner image upload section */}
          <DisplayGrid item xs={12} when={!isBannerVideo}>
            {useSquareBanner ? (
              <ImageUploader
                ref={bannerRef}
                data-test="banner-image-input"
                field={{ form, name: "banner", isUploadingName: "isBannerUploading" }}
                editor={{ kind: "square", targetSize: 800 }}
                layout={{ ratio: Ratio.Square }}
                upload={{
                  context: ImageUploadContext.LiveFormEditorBanner,
                  amplitudePayload,
                  fundraiserContext,
                }}
                library={{
                  allowZeffyImages: true,
                  allowUnsplash: true,
                  defaultTab: "zeffyImages",
                  excludedZeffyImageTags,
                }}
                labels={{
                  import: t("dashboard", "donationForm.uploadBannerV2"),
                  selectImageTitle: t("common", "selectBannerImage"),
                }}
                onImageSourceChange={onBannerSourceChange}
                onImageAltTextMetadataChange={handleAltTextMetadataChange}
              />
            ) : (
              <ImageUploader
                ref={bannerRef}
                data-test="banner-image-input"
                field={{ form, name: "banner", isUploadingName: "isBannerUploading" }}
                editor={{ kind: "crop", maxDimensions: MAX_IMAGE_SIZES["banner"] }}
                layout={{ ratio: Ratio.Landscape }}
                upload={{
                  context: ImageUploadContext.LiveFormEditorBanner,
                  amplitudePayload,
                  fundraiserContext,
                }}
                library={{
                  allowZeffyImages: true,
                  allowUnsplash: true,
                  defaultTab: "zeffyImages",
                  excludedZeffyImageTags,
                }}
                labels={{
                  import: t("dashboard", "donationForm.uploadBannerV2"),
                  selectImageTitle: t("common", "selectBannerImage"),
                }}
                onBeforeReplace={isLegacyBanner ? onLegacyImageChange : undefined}
                onBeforeDelete={isLegacyBanner ? onLegacyImageChange : undefined}
                onImageSourceChange={onBannerSourceChange}
                onImageAltTextMetadataChange={handleAltTextMetadataChange}
              />
            )}
          </DisplayGrid>

          {/* Banner video URL input (if video selected) */}
          <DisplayGrid item xs={12} when={isBannerVideo}>
            <DashboardTextField
              name="bannerVideoUrl"
              control={form.control}
              placeholder={t("dashboard", "donationForm.bannerVideoUrlPlaceholder")}
              size="small"
              errorMessage={(errors["bannerVideoUrl"] as FieldError)?.message}
              rules={[isYoutubeVideo(t("dashboard", "donationForm.videoMustBeOnYoutube"))]}
              onBlur={logUserInputEvent({
                inputName: "bannerVideoUrl",
                isEmpty: !form.getValues("bannerVideoUrl")?.trim?.(),
                isValid: !form.formState.errors.bannerVideoUrl,
              })}
            />
          </DisplayGrid>

          {/* Video preview (if video URL provided) */}
          <DisplayGrid item xs={12} when={Boolean(isBannerVideo)} sx={{ marginTop: 2 }}>
            {Boolean(bannerVideoLink) && bannerVideoLink !== "" && (
              <iframe
                src={`https://www.youtube.com/embed/${getYoutubeIdentifier(
                  bannerVideoLink
                )}?autoplay=1&mute=1&loop=1;showinfo=0`}
                allowFullScreen
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture;"
                width="100%"
                height={400}
              />
            )}
          </DisplayGrid>
        </Stack>
      </LiveEditorBlock>
    </FormStepContainer>
  );
};
